import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/context/auth';
import { useSmsStatus } from '@/context/SmsStatusContext';
import { apiClient } from '@/api/client';
import { toast } from '@/components/ui/use-toast';
import { format, subDays, startOfYear } from 'date-fns';
import { requestCache, createSmsUsageCacheKey, createRestaurantListCacheKey } from '@/utils/request-cache';
import { DateRange } from 'react-day-picker';

export interface SmsUsageRecord {
  restaurant_id: string;
  restaurant_name: string;
  date: string;
  units: number;
  message_count?: number;
}

export interface RestaurantUsageData {
  restaurant_id: string;
  restaurant_name: string;
  total_units: number;
  total_message_count?: number;
  records: SmsUsageRecord[];
}

export interface SmsUsageReportData {
  total: number;
  by_restaurant: RestaurantUsageData[];
}

export interface RestaurantInfo {
  id: string;
  name: string;
  smsEnabled: boolean;
  organisationId: string;
}

export type TimeframeType = '7' | '30' | '90' | '365' | 'ytd' | 'all' | 'custom';

interface UseSmsUsageReportsProps {
  timeframe: TimeframeType;
  selectedRestaurantIds: string[];
  customDateRange?: DateRange;
}

interface UseSmsUsageReportsReturn {
  // Data
  restaurants: RestaurantInfo[];
  usageData: SmsUsageReportData | null;
  
  // Loading states
  loadingRestaurants: boolean;
  loadingUsage: boolean;
  
  // Error states
  error: string | null;
  
  // Actions
  refetch: () => Promise<void>;
  clearError: () => void;
}

/**
 * Unified hook for SMS usage reports that combines restaurant list and usage data fetching
 * Eliminates multiple API calls and provides consistent caching
 */
export const useSmsUsageReports = ({
  timeframe,
  selectedRestaurantIds,
  customDateRange
}: UseSmsUsageReportsProps): UseSmsUsageReportsReturn => {
  const { user } = useAuth();
  const { getRestaurantListWithSmsStatus } = useSmsStatus();

  // Stabilize selectedRestaurantIds to prevent unnecessary re-renders
  const stableSelectedRestaurantIds = useMemo(() =>
    selectedRestaurantIds.sort(),
    [selectedRestaurantIds]
  );

  // State
  const [restaurants, setRestaurants] = useState<RestaurantInfo[]>([]);
  const [usageData, setUsageData] = useState<SmsUsageReportData | null>(null);
  const [loadingRestaurants, setLoadingRestaurants] = useState(true);
  const [loadingUsage, setLoadingUsage] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get date range for the selected timeframe
  const dateRange = useMemo(() => {
    const endDate = new Date();
    let startDate;

    switch (timeframe) {
      case '7':
        startDate = subDays(endDate, 7);
        break;
      case '30':
        startDate = subDays(endDate, 30);
        break;
      case '90':
        startDate = subDays(endDate, 90);
        break;
      case '365':
        startDate = subDays(endDate, 365);
        break;
      case 'ytd':
        startDate = startOfYear(endDate);
        break;
      case 'all':
        // Use a very old date for "all time"
        startDate = new Date('2020-01-01');
        break;
      case 'custom':
        if (customDateRange?.from && customDateRange?.to) {
          return {
            start_date: format(customDateRange.from, 'yyyy-MM-dd'),
            end_date: format(customDateRange.to, 'yyyy-MM-dd')
          };
        }
        // Fallback to 30 days if custom range not set
        startDate = subDays(endDate, 30);
        break;
      default:
        startDate = subDays(endDate, 30);
    }

    return {
      start_date: format(startDate, 'yyyy-MM-dd'),
      end_date: format(endDate, 'yyyy-MM-dd')
    };
  }, [timeframe, customDateRange]);

  // Clear error function
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Fetch restaurants list
  const fetchRestaurants = useCallback(async () => {
    if (!user?.organisationId) {
      setLoadingRestaurants(false);
      return;
    }

    const cacheKey = createRestaurantListCacheKey(user.organisationId);

    try {
      setLoadingRestaurants(true);
      setError(null);
      
      const restaurantsList = await requestCache.getOrFetch(cacheKey, () =>
        getRestaurantListWithSmsStatus(user.organisationId)
      );
      setRestaurants(restaurantsList);
    } catch (err) {
      console.error('Error fetching restaurants:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to load restaurant data';
      setError(errorMessage);
      
      toast({
        title: "Error fetching restaurants",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setLoadingRestaurants(false);
    }
  }, [user?.organisationId, getRestaurantListWithSmsStatus]);

  // Fetch usage data
  const fetchUsageData = useCallback(async () => {
    if (!user?.organisationId) {
      setUsageData(null);
      return;
    }

    const cacheKey = createSmsUsageCacheKey(
      user.organisationId,
      stableSelectedRestaurantIds,
      dateRange.start_date,
      dateRange.end_date
    );

    console.log('[SMS Usage] fetchUsageData called with cache key:', cacheKey);

    try {
      setLoadingUsage(true);
      setError(null);
      
      const usageData = await requestCache.getOrFetch(cacheKey, async () => {
        console.log('[SMS Usage] Starting API call to /api/sms/usage/combined');
        
        // Try the new combined API endpoint first
        try {
          const response = await apiClient.get('/api/sms/usage/combined', {
            params: {
              organisation_id: user.organisationId,
              restaurant_ids: stableSelectedRestaurantIds.length > 0 ? stableSelectedRestaurantIds.join(',') : undefined,
              start_date: dateRange.start_date,
              end_date: dateRange.end_date
            }
          });

          console.log('[SMS Usage] Combined API response:', response.data);

          if (response.data?.success && response.data?.usage) {
            return response.data.usage;
          } else {
            throw new Error(response.data?.message || 'Failed to fetch usage data from combined endpoint');
          }
        } catch (combinedError) {
          console.warn('[SMS Usage] Combined endpoint failed, falling back to direct RPC call:', combinedError);
          
          // Fallback to direct Supabase RPC call (existing behavior)
          const { supabase } = await import('@/integrations/supabase/client');
          
          console.log('[SMS Usage] Using fallback RPC call to get_sms_usage_from_campaigns');
          
          // Get all restaurants for this organisation
          const { data: restaurants, error: restError } = await supabase
            .from('restaurants_master')
            .select('restaurant_id, restaurant_name')
            .eq('organisation_id', user.organisationId);
          
          if (restError) {
            throw new Error(`Failed to fetch restaurants: ${restError.message}`);
          }
          
          // Extract restaurant IDs
          const allRestaurantIds = restaurants?.map(r => r.restaurant_id) || [];
          
          // Call the RPC function to get SMS usage data directly from Supabase (including org campaigns)
          const { data: usageRecords, error: usageError } = await supabase.rpc(
            'get_sms_usage_from_campaigns',
            {
              restaurant_ids: stableSelectedRestaurantIds.length > 0 ? stableSelectedRestaurantIds : allRestaurantIds,
              param_organisation_id: user.organisationId, // Pass organisation_id to include org-wide campaigns
              start_date: dateRange.start_date,
              end_date: dateRange.end_date
            }
          );
          
          if (usageError) {
            throw new Error(`Failed to get SMS usage: ${usageError.message}`);
          }
          
          if (usageRecords) {
            // Format the data to match the expected structure from the API
            const usage = {
              total: usageRecords.reduce((sum, record) => sum + (record.units || 0), 0),
              by_restaurant: []
            };
            
            // Group by restaurant
            const restaurantMap = new Map();
            
            // First organize data by restaurant
            for (const record of usageRecords) {
              if (!restaurantMap.has(record.restaurant_id)) {
                restaurantMap.set(record.restaurant_id, {
                  restaurant_id: record.restaurant_id,
                  restaurant_name: record.restaurant_name || 'Unknown Restaurant',
                  total_units: 0,
                  total_message_count: 0,
                  records: []
                });
              }

              const restaurant = restaurantMap.get(record.restaurant_id);
              restaurant.total_units += (record.units || 0);
              restaurant.total_message_count += (record.message_count || 0);
              restaurant.records.push({
                restaurant_id: record.restaurant_id,
                restaurant_name: record.restaurant_name,
                date: record.date,
                units: record.units || 0,
                message_count: record.message_count || 0
              });
            }
            
            // Convert the map to an array
            for (const restaurantData of restaurantMap.values()) {
              usage.by_restaurant.push(restaurantData);
            }
            
            return usage;
          } else {
            return { total: 0, by_restaurant: [] };
          }
        }
      });

      setUsageData(usageData);
    } catch (err) {
      console.error('Error fetching usage data:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to load usage data';
      setError(errorMessage);
      
      // Don't show toast for every error to avoid spam
      if (err instanceof Error && !err.message.includes('No usage data')) {
        toast({
          title: "Error loading usage data",
          description: errorMessage,
          variant: "destructive"
        });
      }
    } finally {
      setLoadingUsage(false);
    }
  }, [user?.organisationId, timeframe, stableSelectedRestaurantIds, dateRange.start_date, dateRange.end_date]);

  // Combined refetch function
  const refetch = useCallback(async () => {
    // Clear request cache to force fresh fetch
    if (user?.organisationId) {
      const restaurantCacheKey = createRestaurantListCacheKey(user.organisationId);
      const usageCacheKey = createSmsUsageCacheKey(
        user.organisationId,
        stableSelectedRestaurantIds,
        dateRange.start_date,
        dateRange.end_date
      );
      requestCache.invalidate(restaurantCacheKey);
      requestCache.invalidate(usageCacheKey);
    }
    
    await Promise.all([
      fetchRestaurants(),
      fetchUsageData()
    ]);
  }, [fetchRestaurants, fetchUsageData, user?.organisationId, stableSelectedRestaurantIds, dateRange.start_date, dateRange.end_date]);

  // Effect for fetching restaurants (only once per organisation)
  useEffect(() => {
    console.log('[SMS Usage] useEffect: fetchRestaurants triggered');
    fetchRestaurants();
  }, [fetchRestaurants]);

  // Effect for fetching usage data (when parameters change)
  useEffect(() => {
    console.log('[SMS Usage] useEffect: fetchUsageData triggered', {
      restaurantsLength: restaurants.length,
      fetchUsageDataFunction: !!fetchUsageData
    });
    if (restaurants.length > 0) {
      fetchUsageData();
    }
  }, [fetchUsageData, restaurants.length]);

  return {
    // Data
    restaurants,
    usageData,
    
    // Loading states
    loadingRestaurants,
    loadingUsage,
    
    // Error states
    error,
    
    // Actions
    refetch,
    clearError
  };
};